import { isOrderWaitingOnSeller } from '../../transactions/transaction';

/**
 * Per-listing chip counts for "Dina annonser" (P25009-389 BR-2).
 *
 * Pure on purpose. The four bugs manual testing found during P25009-385 were all
 * in numbers like these, and all four passed their unit tests because the
 * fixtures encoded the same wrong assumption as the code. Keeping the derivation
 * pure and feeding it shapes taken from real API responses is the only way these
 * get checked rather than merely covered.
 *
 * Every function returns a plain object keyed by listing id, so a row looks its
 * own count up in constant time instead of filtering a shared array.
 */

/** Read the listing id off a raw (non-denormalised) transaction resource. */
const listingIdOf = tx => tx?.relationships?.listing?.data?.id?.uuid || null;

/**
 * Unread messages per listing, seller side.
 *
 * Counts the `providerHasUnreadMessages` metadata flag — the same field
 * `/inbox/sales?filter=unread` filters on. The chip links to that page, so the
 * number has to come from the same field or the row and the page it opens
 * contradict each other.
 *
 * The obvious alternative, comparing each message's timestamp against
 * `providerLastVisit`, is what this replaces. It read "this sale was never
 * opened" as "unread message": on WHEELPLACE Test it produced far more rows than
 * the inbox showed, including sales from 2024 with no message dates at all. The
 * flag reports 9 unread across 6 listings, which is what the inbox shows.
 *
 * @param {Array} transactions raw transaction resources (`only: 'sale'`)
 * @returns {Object} { [listingId]: count }
 */
export const unreadMessagesByListing = transactions =>
  (transactions || []).reduce((acc, tx) => {
    if (tx?.attributes?.metadata?.providerHasUnreadMessages !== true) return acc;
    const listingId = listingIdOf(tx);
    if (!listingId) return acc;
    acc[listingId] = (acc[listingId] || 0) + 1;
    return acc;
  }, {});

/**
 * New orders per listing — paid, and still waiting on the seller.
 *
 * Two conditions, both required:
 *
 *   1. `isOrderWaitingOnSeller` — the shared allowlist of
 *      `transition/confirm-payment`. Without it this counted cancelled orders,
 *      abandoned checkouts and plain questions: 165 of 265 transactions on
 *      WHEELPLACE Test, against a true figure of 1.
 *   2. the seller has not opened it since it last moved, so the chip clears when
 *      the order is dealt with rather than sitting there for ever.
 *
 * @param {Array} transactions raw transaction resources (`only: 'sale'`)
 * @returns {Object} { [listingId]: count }
 */
export const newOrdersByListing = transactions =>
  (transactions || []).reduce((acc, tx) => {
    const attributes = tx?.attributes;
    if (!isOrderWaitingOnSeller(attributes)) return acc;

    const providerLastVisit = attributes.metadata?.providerLastVisit;
    const movedAt = new Date(attributes.lastTransitionedAt).getTime();
    const unseen = !providerLastVisit || movedAt > providerLastVisit;
    if (!unseen) return acc;

    const listingId = listingIdOf(tx);
    if (!listingId) return acc;
    acc[listingId] = (acc[listingId] || 0) + 1;
    return acc;
  }, {});

/**
 * How little stock counts as "running out".
 *
 * One left is the point at which the next sale takes the listing off the
 * marketplace, which is when the seller wants to know. Same threshold as the
 * dashboard's low-stock row (SellerDashboardPage.duck LOW_STOCK_THRESHOLD), and
 * the value P25009-389 asks for on the chip ("warning style at n <= 1").
 */
export const LOW_STOCK_THRESHOLD = 1;

/**
 * Does this listing's stock deserve a warning?
 *
 * Only for listings that actually track stock. `currentStock` is absent for
 * booking and inquiry listing types, and treating "not tracked" as zero would
 * put a red warning on every one of them.
 *
 * @param {number|undefined|null} quantity
 */
export const isLowStock = quantity =>
  typeof quantity === 'number' && quantity <= LOW_STOCK_THRESHOLD;

/**
 * Is this listing about to stop selling?
 *
 * The rule behind "Snart slut i lager" on the listings page and "Fyll på lagret"
 * on the dashboard. It lives here because those are computed by two different
 * ducks — Sharetribe cannot filter own listings by stock level, so each has to
 * walk its own page loop — and when the rule was written out twice the two
 * surfaces disagreed in public: the dashboard said "9 annonser tar slut efter
 * nästa order" while the listings page said 3.
 *
 * All six extra rows were hidden listings. Only a LIVE listing can run out in a
 * way that costs a sale: a hidden one is off the marketplace and nobody can
 * order it, a draft was never for sale, and a removed one is gone. "Published"
 * on its own is not enough.
 *
 * Takes plain values rather than a listing entity, because one caller reads a
 * raw API response and the other a denormalised one.
 *
 * @param {Object} params
 * @param {string} params.state       listing state
 * @param {boolean} params.isHide     publicData.isHide
 * @param {boolean} params.isDeleted  publicData.isDeleted
 * @param {number|undefined} params.quantity  currentStock quantity, if tracked
 */
export const isRunningOut = ({ state, isHide, isDeleted, quantity }) =>
  state === 'published' && isHide !== true && isDeleted !== true && isLowStock(quantity);
